/* eslint-disable no-empty */
import i18next from 'i18next';
import type { AnyFunction } from '@hotjar/webapp/lib/function';
import { predicate } from '@hotjar/webapp/lib/function';
import type { ExtendedCurrentUser } from '@hotjar/webapp/domains/common/user-data/model';

// FIXME: camelcase
/* eslint-disable camelcase */
export const FormSubmittedEventData = (
  app_form_name: string,
  app_form_submit_success: boolean,
  groupData: CustomEventProperties,
): SegmentTrackData => ({
  app_form_name,
  app_form_submit_success,
  ...groupData,
});

export const SubscriptionPausedEventData = (
  account_id: string,
  user_id: string,
  pause_reason: string,
): SegmentTrackData => ({
  account_id,
  user_id,
  pause_reason,
});

// FIXME: Please fix this. You may refer to CFE-266 for more details.
// eslint-disable-next-line func-params-args/func-params
export const ChurnSurveySubmittedEventData = (
  account_id: string,
  user_id: string,
  downgrade_reason: string,
  downgrade_explanation: string,
  likely_to_upgrade: boolean,
): SegmentTrackData => ({
  account_id,
  user_id,
  downgrade_reason,
  downgrade_explanation,
  likely_to_upgrade,
});

type Callback = () => void;

type NumericId = string | number;
type UserId = NumericId;
type SiteId = NumericId;

type PromiseExecutor<TResolve, TReject> = (
  resolve: (value?: TResolve) => void,
  reject: (value?: TReject) => void,
) => void;

interface Options {
  context: {
    ip?: string;
    location?: {
      country: string;
    };
    locale?: string;
  };
}

interface Traits {
  /* eslint-disable camelcase */
  last_accessed_site_id?: SiteId;
  /* eslint-enable camelcase */
  createdAt?: string;
}

export interface EventProperties {
  /* eslint-disable camelcase */
  state_event_was_triggered_on?: string;
  url_event_was_triggered_on?: string;
  site_id?: number | string;
  organization_id?: number | string;
  account_id?: number | string;
  /* eslint-enable camelcase */
}

type ValidPropertyTypes = string | number | boolean;

export interface CustomEventProperties {
  [property: string]: ValidPropertyTypes | ValidPropertyTypes[];
}

interface EnrichedEvent {
  /* eslint-disable camelcase */
  state_event_was_triggered_on: string;
  url_event_was_triggered_on: string;
  site_id?: string;
  organization_id?: string;
  account_id?: string;
  locale?: string;
  geo_country?: string;
  /* eslint-enable camelcase */
}

interface State {
  current: {
    name: string;
  };
}

export type SegmentTrackData = EventProperties & CustomEventProperties;

interface Analytics {
  // FIXME: Please fix this. You may refer to CFE-266 for more details.
  // eslint-disable-next-line func-params-args/func-params
  identify(userId: UserId, traits: Traits, options: Options, callback: Callback): void;
  // FIXME: Please fix this. You may refer to CFE-266 for more details.
  // eslint-disable-next-line func-params-args/func-params
  alias(userId: UserId, something: undefined, options: Options, callback: Callback): void;
  // FIXME: Please fix this. You may refer to CFE-266 for more details.
  // eslint-disable-next-line func-params-args/func-params
  track(eventName: string, event: EnrichedEvent, options: Options, callback: Callback): void;
  // FIXME: Please fix this. You may refer to CFE-266 for more details.
  // eslint-disable-next-line func-params-args/func-params
  page(
    something: undefined,
    name: string,
    somethingElse: Pick<EnrichedEvent, 'geo_country'>,
    options: Options,
    callback: Callback,
  ): void;
  user(): {
    anonymousId(id: UserId): void;
    anonymousId(): UserId;
    id(id: UserId): void;
    id(): UserId;
  };
  ready(callback: Callback): void;
  reset(): void;
}

declare global {
  // eslint-disable-next-line no-var, vars-on-top
  var analytics: Analytics;
}

const SEGMENT_READY_TIMEOUT = 2000;
const OPERATION_TIMEOUT = 2000;

// If we are unable to get the visotor's anonymized IP from our back-end,
// we set it to zero to prevent leaking this information to Segment.
const FALLBACK_IP = '0.0.0.0';

let anonymizedIp: string | undefined;
let countryCode: string | undefined;
// NOTE: to get rid of this we'd need to rewrite tests
// for now lets not touch them and make sure they're all passing
let stateImplementation: State = { current: { name: '' } };
let LogImplementation = console;
let locationHrefGetter = () => window.location.href;

const addOptions = (): Options => {
  const ip = anonymizedIp ?? FALLBACK_IP;
  const locale = i18next.resolvedLanguage ?? 'en';

  return {
    context: {
      ...(ip && { ip }),
      ...(typeof countryCode !== 'undefined' && { location: { country: countryCode } }),
      locale,
    },
  };
};

const checkIfTestEnvironment = () => {
  if (global.getBasedOnEnvironment({ test: true, default: false })) {
    LogImplementation.debug('Segment: skipping since we are in the test environment');
    return false;
  }
  return true;
};

/**
 * Decorator used to skip any Segment calls if we're in the 'test'
 * environment.
 *
 * If we allow Segment calls during tests then our e2e test will create
 * thousands of Segment events.
 */
const shouldRun = <TFunction extends AnyFunction>(
  fn: TFunction,
  defaultVal: ReturnType<TFunction>,
) => predicate(fn, [checkIfTestEnvironment], defaultVal);

// Since async functions are expected to return a Promise, we have a default
// value of a resolved promise, such that Promise chains will be un-affected
// if Segment calls should not be run.
const defaultAsyncValue = Promise.resolve();

const withTimeout = async <TResolve, TReject>(
  fn: PromiseExecutor<TResolve, TReject>,
  timeoutInMsec = OPERATION_TIMEOUT,
): Promise<TResolve | unknown> => {
  let rejectionTimeout: NodeJS.Timeout;
  return Promise.race([
    new Promise(fn),
    new Promise((resolve, reject) => {
      rejectionTimeout = setTimeout(() => {
        reject(
          new Error(
            `${fn.name || 'Anonymous function'} failed to resolve in ${timeoutInMsec} milliseconds`,
          ),
        );
      }, timeoutInMsec);
    }),
  ]).finally(() => clearTimeout(rejectionTimeout));
};

const whenReady = () =>
  withTimeout((resolve) => global.analytics.ready(resolve), SEGMENT_READY_TIMEOUT);

const shouldRunWhenReady = <TFunction extends AnyFunction>(fn: TFunction) =>
  shouldRun(
    (...args: Parameters<TFunction>): Promise<ReturnType<TFunction>> =>
      whenReady().then(
        // eslint-disable-next-line prefer-spread
        () => fn.apply(null, args),
        () => LogImplementation.debug('Segment: failed loading integrations.'),
      ),
    defaultAsyncValue as Promise<ReturnType<TFunction>>,
  );

const eventsAllowedToGetLocale = [
  'Recording List Viewed',
  'Element Viewed',
  'Trend Viewed',
  'Funnel Viewed',
  'Dashboard Viewed',
];

/**
 * Enrich an event with base and grouping properties.
 */
function enrichEvent<TProperties extends EventProperties & CustomEventProperties>(
  eventName: string,
  properties: TProperties,
): EnrichedEvent & CustomEventProperties {
  const locale = i18next.resolvedLanguage ?? 'en';

  const enrichedEvent: EnrichedEvent = {
    /* eslint-disable no-param-reassign */
    state_event_was_triggered_on:
      properties.state_event_was_triggered_on ?? stateImplementation.current.name,
    url_event_was_triggered_on: properties.url_event_was_triggered_on ?? locationHrefGetter(),

    // Grouping is done by using the properties below and prefixing them.
    // This allows us to ensure we can group things properly in analytics tools we use.
    ...(properties.site_id !== undefined && { site_id: `site-${properties.site_id}` }),
    ...(properties.organization_id !== undefined && {
      organization_id: `org-${properties.organization_id}`,
    }),
    ...(properties.account_id !== undefined && { account_id: `acc-${properties.account_id}` }),
    ...(eventsAllowedToGetLocale.includes(eventName) && { locale }),
    ...(typeof countryCode !== 'undefined' && { geo_country: countryCode }),
    /* eslint-enable no-param-reassign */
  };

  return Object.assign(properties, enrichedEvent);
}

const segmentErrorHandler = (error: Error) => {
  const log = `Segment: ${error}`;
  LogImplementation.debug(log);
};

/** ***********************
 * PUBLIC METHODS
 ************************ */

/**
 * Identify the user with Segment.
 *
 * All the traits are sent as part of a backend call to make it more reliable in case of ad-blockers etc.
 * This only exists because of the few events we track client side, so they can be attributed to
 * the right user.
 *
 * The only exception is `last_accessed_site_id` which is only a thing on the frontend.
 */
export const identify = shouldRunWhenReady(function identify(userData: ExtendedCurrentUser) {
  return withTimeout((resolve) => {
    const traits: Traits = {};

    if (userData.currentSite?.id !== undefined) {
      traits.last_accessed_site_id = userData.currentSite?.id;
    }

    if (userData.userCreated) {
      traits.createdAt = new Date(userData.userCreated * 1000).toISOString();
    }

    global.analytics.identify(userData.userId, traits, addOptions(), resolve);
  }).catch(segmentErrorHandler);
});

export const setAnonymousUserID = shouldRunWhenReady(function setAnonymousUserID(
  anonymousID: UserId,
) {
  return global.analytics.user().anonymousId(anonymousID);
});

export const getAnonymousUserID = shouldRunWhenReady<() => NumericId>(
  function getAnonymousUserID() {
    return global.analytics.user().anonymousId();
  },
);

export const getUserID = shouldRunWhenReady(function getUserId() {
  return global.analytics.user().id() ?? global.analytics.user().anonymousId();
});

export const isIdentified = shouldRunWhenReady(() => {
  return global.analytics.user().id();
});

/**
 * Alias a user Id with a previous anonymous user we were tracking.
 *
 * @param userId ID of user to associate anonymous user/events to.
 */
export const alias = shouldRunWhenReady(function alias(userId: UserId) {
  return withTimeout((resolve) =>
    global.analytics.alias(userId, undefined, addOptions(), resolve),
  ).catch(segmentErrorHandler);
});

/**
 * Reset() dissociates future events that occur from the user currently identified.
 * When run, user_id is nullified and a new anonymousId is assigned to this visitor.
 *
 * This is typically executed on logout so non-logged-in events are no longer attributed to the user.
 * Without this, if the user logged out and registered a new account, segment would attribute
 * the signup step events to the user_id the visitor had before logging out.
 */
export const reset = shouldRunWhenReady(function reset() {
  return global.analytics.reset();
});

/**
 * Logout user from tracking if already identified.
 */
export const logoutIfAlreadyIdentified = shouldRunWhenReady(() => {
  if (global.analytics.user().id() !== undefined) {
    return global.analytics.reset();
  }

  return undefined;
});

/**
 * Track an event.
 *
 * @param eventName Name of event.
 * @param properties Object with details about the event.
 */
export const track = shouldRunWhenReady<(eventName: string, properties?: SegmentTrackData) => void>(
  function track(eventName: string, properties: SegmentTrackData = {}) {
    return withTimeout<void, void>((resolve) =>
      global.analytics.track(eventName, enrichEvent(eventName, properties), addOptions(), resolve),
    ).catch(segmentErrorHandler);
  },
);

/**
 * Track a page view.
 *
 * @param name URL, path or other identifier for the page.
 */
export const page = shouldRunWhenReady<(name: string) => void>(function page(name: string) {
  const enrichedPageEvent = {
    ...(typeof countryCode !== 'undefined' && { geo_country: countryCode }),
  };

  return withTimeout((resolve) =>
    global.analytics.page(undefined, name, enrichedPageEvent, addOptions(), resolve),
  ).catch(segmentErrorHandler);
});

/**
 * Set the IP that will be sent to Segment to enable country reporting.
 *
 * @param _currentIp IP, user's IP obtained from back-end. We anonymize by setting the last ocet set to zero.
 */
export const setAnonymizedUserId = (_currentIp: string | undefined): void => {
  if (_currentIp === undefined || _currentIp.indexOf('.') < 0) {
    // Current IP is undefined or is IPv6.
    anonymizedIp = _currentIp;
  } else {
    // Anonymize the IP by setting the last ocet set to zero.
    const ipParts = _currentIp.split('.');
    ipParts[ipParts.length - 1] = '0';
    anonymizedIp = ipParts.join('.');
  }
};

/**
 * Set the country code that will be sent to Segment to enable country reporting.
 *
 * @param _countryCode Country Code, user's country code Must be obatined from back-end.
 */
export const setCountryCode = (_countryCode: typeof countryCode): void => {
  countryCode = _countryCode;
};

export const setStateImplementation = (_stateImplementation: State): void => {
  stateImplementation = _stateImplementation;
};

export const setLogImplementation = (_LogImplementation: typeof console): void => {
  LogImplementation = _LogImplementation;
};

export const setLocationHrefGetter = (_locationHrefGetter: () => string): void => {
  locationHrefGetter = _locationHrefGetter;
};
